import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";

import {useTheme} from "../../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../../connectui/component/buildStyles";
import ICONS from "../../../../../connectui/icons/iconMap";

import Container from "../../../../../connectui/component/layout/Container";
import DropdownExpand from "../../../../../connectui/component/display/dropdown/DropdownExpand";
import FlexContainer from "../../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../../connectui/component/display/Text";
import ChipIconText from "../../../../../connectui/component/display/chip/ChipIconText";
import ChipBase from "../../../../../connectui/component/display/chip/base/ChipBase";
import ChipIcon from "../../../../../connectui/component/display/chip/ChipIcon";
import SvgIcon from "../../../../../connectui/component/display/SvgIcon";
import CircularChipBase from "../../../../../connectui/component/display/chip/base/CircularChipBase";
import ListComponent from "../../../../../connectui/component/layout/list/ListComponent";
import ContextMenu from "../../../../../connectui/component/input/context/ContextMenu";
import Skeleton from "../../../../../connectui/component/display/Skeleton";
import StatusIcon from "../../../../../connectui/component/display/StatusIcon";
import CircularSpinner from "connectui/component/display/CircularSpinner";

const useStyles = (theme) =>
  buildStyles({
    cardContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      padding: theme.spacing(2, 3),
      borderBottom: `1px solid ${theme.colors.border}`,
    },
    collapsedIcon: {
      width: "17px",
      height: "17px",
    },
    moreIcon: {
      width: "20px",
      height: "20px",
      marginRight: theme.spacing(2),
      borderRadius: "50%",
      cursor: "pointer",
      padding: theme.spacing(0.5),
      "&:hover": {
        backgroundColor: theme.colors.gray100,
      },
    },
  });

const baseIcons = {
  "Enter details": ICONS.list,
  "Id upload": ICONS["id-card"],
  Agreement: ICONS["file-text"],
  Fees: ICONS["coin-stack"],
  "Check In": ICONS["check-in"],
  "Check Out": ICONS["check-out"],
};

function getActionTranslations(label) {
  switch (label) {
    case "Skip":
    case "Reset":
    case "Cancel":
    case "Release":
    case "Refund":
      return {
        title: label,
        description: `Action: ${label}`,
      };
    default:
      return {
        title: label,
        description: "",
      };
  }
}

const BoardingPassCard = ({booking, handleAction = () => {}}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const stepSubMenuItemClicked = useRef(false);
  const loadingCreateAction = useSelector(
    (state) => state.defaultReducer.loading,
  ).create_action;
  const [bpDropdownActions, setBpDropdownActions] = useState([]);
  const [bpDropdownAnchorEl, setBpDropdownAnchorEl] = useState(null);
  const [stepsAnchorEl, setStepsAnchorEl] = useState(null);
  const [selectedActions, setSelectedActions] = useState([]);
  const [selectedSubItems, setSelectedSubItems] = useState([]);
  const [vfActions, setVfActions] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [stepsSubMenuAnchorEl, setStepsSubMenuAnchorEl] = useState(null);
  const [subMenuPosition, setSubMenuPosition] = useState({top: 0, left: 0});
  const [loadingAction, setLoadingAction] = useState({
    index: null,
    type: null,
  });
  // InstantActionsPOST in progress OR any of the BP actions is loading
  const isActionInProgress =
    loadingCreateAction || loadingAction.index !== null;

  const isVerified = !!booking.tags?.includes("Verified");
  const isRevoked = !!booking.tags?.includes("Revoked");
  const guestBPStatus = {
    text: isRevoked ? "Revoked" : isVerified ? "Verified" : "Unverified",
    bg: isRevoked
      ? theme.colors.error50
      : isVerified
        ? theme.colors.success50
        : theme.colors.gray100,
    textColor: isRevoked
      ? theme.colors.error700
      : isVerified
        ? theme.colors.success700
        : theme.colors.gray700,
    dotColor: isRevoked
      ? theme.colors.error
      : isVerified
        ? theme.colors.success
        : theme.colors.gray,
  };

  useEffect(() => {
    const steps = booking?.actions?.vf_steps || [];
    setVfActions(steps);
    setActiveStep(steps.findIndex((st) => st.status_label === "incomplete"));
  }, [booking]);

  useEffect(() => {
    const newBpActions = (booking?.actions?.bp_dropdown || []).map(
      (bpAction, index) => {
        const label = bpAction.label || "Action";

        const isVerificationReport =
          bpAction.label === "Download Proof of Verification" ||
          (bpAction.action_options &&
            bpAction.action_options.action_id === "verification_report");

        const disableVerificationReport = isVerificationReport && !isVerified;

        return {
          label,
          onClick: handleActionClick(bpAction, index, "bp_dropdown"),
          disabled: isActionInProgress || disableVerificationReport,

          sx: disableVerificationReport
            ? {
                color: theme.colors.disabled,
                opacity: 0.6,
                cursor: "not-allowed",
                pointerEvents: "none",
                backgroundColor: "transparent !important",
              }
            : undefined,
        };
      },
    );
    setBpDropdownActions(newBpActions);
  }, [booking, isVerified]);

  const handleActionClick =
    (action, index, type = null, subItems = null) =>
    (e) => {
      if (subItems) {
        // Get item position and open submenu to display options
        const rect = e.currentTarget.getBoundingClientRect();
        setSubMenuPosition({
          top: rect.top,
          right: window.innerWidth - rect.left,
        });
        setStepsSubMenuAnchorEl(e.currentTarget);
        setSelectedSubItems(subItems);
      } else {
        // Trigger the action
        const action_type = null;
        setLoadingAction({index, type});
        handleAction(action, action_type, () =>
          setLoadingAction({index: null, type: null}),
        );
        closeStepsSubMenu();
        closeStepsMenu();
        setBpDropdownAnchorEl(null);
      }
    };

  const closeStepsMenu = () => {
    // Don't close main menu if user is clicking inside the submenu
    if (stepSubMenuItemClicked.current) return;

    setStepsAnchorEl(null);
    setSelectedActions([]);
    closeStepsSubMenu();
  };

  const closeStepsSubMenu = () => {
    stepSubMenuItemClicked.current = false;
    setStepsSubMenuAnchorEl(null);
    setSelectedSubItems([]);
  };

  function getStepProps(action, index) {
    const isFee = !!action.detailed_statuses_labels;

    const actionsArray = action.actions.map((act) => {
      const translations = getActionTranslations(act.label);
      const options = isFee
        ? act.options.map((opt) => ({
            label: opt.label,
            title: opt.label,
            onClick: handleActionClick(opt, index, "vf_steps"),
          }))
        : undefined;

      return {
        variant: act.variant,
        title: translations.title,
        description: translations.description,
        label: act.label,
        disabled: isActionInProgress,
        options: options,
        onClick: handleActionClick(act, index, "vf_steps", options),
        action_options: act.action_options, // Store the full action_options object
      };
    });

    return {
      label: action.label,
      status: index === activeStep ? "active" : action.status_label,
      actions: actionsArray,
    };
  }

  function getIconForStep(step) {
    const iconSize = {width: "15px", height: "15px"};
    if (step.status === "active") {
      return (
        <SvgIcon
          component={baseIcons[step.label]}
          sx={{color: theme.colors.primary, ...iconSize}}
        />
      );
    }
    if (["complete", "bypassed"].includes(step.status)) {
      return (
        <SvgIcon
          component={ICONS["check"]}
          sx={{...iconSize, color: theme.colors.white}}
        />
      );
    }
    return (
      <SvgIcon
        component={baseIcons[step.label]}
        sx={{
          ...iconSize,
          color:
            step.status === "disabled"
              ? theme.colors.textTertiary
              : theme.colors.textSecondary,
        }}
      />
    );
  }

  function getChipStyles(step) {
    const chipStyles = {
      textColor: theme.colors.gray700,
      bg: theme.colors.hoverGray,
      bgIcon: theme.colors.hoverGray,
    };
    if (["complete", "bypassed"].includes(step.status)) {
      chipStyles.textColor = theme.colors.success700;
      chipStyles.bg = theme.colors.success50;
      chipStyles.bgIcon = theme.colors.success500;
    } else if (step.status === "active") {
      chipStyles.textColor = theme.colors.primary700;
      chipStyles.bg = theme.colors.primary50;
      chipStyles.bgIcon = theme.colors.primary50;
    } else if (step.status === "disabled") {
      chipStyles.textColor = theme.colors.textTertiary;
    }

    return {
      chip: {
        textColor: chipStyles.textColor,
        backgroundColor: chipStyles.bg,
        "&:hover": {backgroundColor: chipStyles.bg},
      },
      icon: {
        backgroundColor: chipStyles.bgIcon,
        border: "none",
        "&:hover": {backgroundColor: chipStyles.bgIcon},
      },
    };
  }

  function collapsedGuestJourney() {
    return (
      <FlexContainer
        justifyContent={"space-between"}
        sx={{
          padding: `${theme.spacing(3)} ${theme.spacing(5)}`,
          paddingBottom: 0,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {vfActions.map((s, i) => {
          const stepProps = getStepProps(s, i);
          const styles = {...getChipStyles(stepProps).icon, cursor: "pointer"};
          return (
            <ChipIcon
              key={i}
              chipBase={CircularChipBase}
              chipSize="24px"
              sx={styles}
              icon={getIconForStep(stepProps)}
            />
          );
        })}
      </FlexContainer>
    );
  }

  function expandedGuestJourney() {
    return (
      <FlexContainer
        sx={{width: "100%", paddingTop: theme.spacing(4), position: "relative"}}
      >
        <ListComponent
          sx={{width: "100%", position: "relative"}}
          gap={theme.spacing(6)}
        >
          {vfActions.map((s, i) => {
            const stepProps = getStepProps(s, i);
            const isLastStep = i === vfActions.length - 1;

            return (
              <FlexContainer
                key={i}
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                sx={{position: "relative"}}
              >
                {/* Vertical connector */}
                {!isLastStep && (
                  <Container
                    style={{
                      position: "absolute",
                      left: "11px",
                      top: "24px",
                      height: "100%",
                      width: "2px",
                      backgroundColor: ["complete", "bypassed"].includes(
                        stepProps.status,
                      )
                        ? theme.colors.success500
                        : theme.colors.hoverGray,
                    }}
                  />
                )}

                {/* Step icon & name */}
                <FlexContainer
                  flexDirection="row"
                  alignItems="center"
                  gap={theme.spacing(2)}
                >
                  {isActionInProgress &&
                  loadingAction.index === i &&
                  loadingAction.type === "vf_steps" ? (
                    <CircularSpinner size="24px" />
                  ) : (
                    <ChipIcon
                      chipBase={CircularChipBase}
                      chipSize="24px"
                      sx={getChipStyles(stepProps).icon}
                      icon={getIconForStep(stepProps)}
                    />
                  )}
                  <Text variant="textSM" weight="medium">
                    {stepProps.label}
                  </Text>
                </FlexContainer>

                {/* Step status + actions */}
                <FlexContainer gap={theme.spacing(2)} alignItems={"center"}>
                  <ChipIconText
                    icon={
                      <StatusIcon
                        color={getChipStyles(stepProps).chip.textColor}
                      />
                    }
                    textProps={{
                      sx: {textTransform: "capitalize"},
                      color: getChipStyles(stepProps).chip.textColor,
                    }}
                    text={stepProps.status}
                    typographyVariant="textXS"
                    weight="medium"
                    sx={getChipStyles(stepProps).chip}
                    chipBase={ChipBase}
                  />
                  {!!stepProps.actions?.length && (
                    <SvgIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isActionInProgress) return;
                        if (!stepsAnchorEl) {
                          setSelectedActions(stepProps.actions);
                          setStepsAnchorEl(e.currentTarget);
                        }
                      }}
                      component={ICONS["more-vertical"]}
                      className={classes.moreIcon}
                      sx={{color: theme.colors.textSecondary}}
                    />
                  )}
                </FlexContainer>
              </FlexContainer>
            );
          })}
        </ListComponent>
      </FlexContainer>
    );
  }

  return (
    <Container className={classes.cardContainer}>
      {/* General Boarding Pass actions */}
      {!!bpDropdownAnchorEl && (
        <ContextMenu
          open
          onClose={() => setBpDropdownAnchorEl(null)}
          anchorEl={bpDropdownAnchorEl}
          menuItems={bpDropdownActions}
        />
      )}

      {/* Step context menu */}
      {!!stepsAnchorEl && (
        <ContextMenu
          open
          onClose={closeStepsMenu}
          anchorEl={stepsAnchorEl}
          menuItems={selectedActions.reduce((acc, act) => {
            const isVerificationReport =
              act.title === "Download Proof of Verification" ||
              act.label === "Download Proof of Verification" ||
              (act.action_options &&
                act.action_options.action_id === "verification_report");

            const disableVerificationReport =
              isVerificationReport && !isVerified;

            acc.push({
              label: act.title,
              onClick: disableVerificationReport ? () => {} : act.onClick,
              disabled: isActionInProgress || disableVerificationReport,
              actions: !!act.options?.length ? (
                <SvgIcon
                  sx={{
                    width: "7px",
                    height: "7px",
                    color: theme.colors.textTertiary,
                    margin: theme.spacing(0, -1, 0, 4),
                  }}
                  component={ICONS["chevron-right"]}
                />
              ) : undefined,

              sx: disableVerificationReport
                ? {
                    color: theme.colors.disabled,
                    opacity: 0.6,
                    cursor: "not-allowed",
                    pointerEvents: "none",
                    backgroundColor: "transparent !important",
                    "&:hover": {
                      backgroundColor: "transparent !important",
                    },
                  }
                : undefined,
            });
            return acc;
          }, [])}
        />
      )}

      {/* Step context submenu */}
      {!!stepsSubMenuAnchorEl && (
        <ContextMenu
          open
          menuItems={selectedSubItems.reduce((acc, subItem) => {
            acc.push({
              label: subItem.title,
              onClick: subItem.onClick,
              disabled: isActionInProgress,
              onMouseDown: () => {
                stepSubMenuItemClicked.current = true;
              },
            });
            return acc;
          }, [])}
          onClose={closeStepsSubMenu}
          anchorEl={stepsSubMenuAnchorEl}
          modalProps={{customPosition: subMenuPosition}}
        />
      )}

      <DropdownExpand
        renderCollapsed
        customLabel={
          <FlexContainer
            justifyContent="space-between"
            alignItems="center"
            sx={{width: "100%"}}
          >
            <FlexContainer alignItems="center" gap={theme.spacing(1)}>
              <SvgIcon
                component={ICONS["boarding-pass-ticket"]}
                sx={{
                  width: "20px",
                  height: "20px",
                  color: theme.colors.textPrimary,
                  marginRight: theme.spacing(0.5),
                }}
              />
              <Text variant="textSM" weight="medium">
                Boarding Pass
              </Text>
              <ChipIconText
                text={guestBPStatus.text}
                textColor={guestBPStatus.textColor}
                typographyVariant="textXS"
                weight="medium"
                icon={<StatusIcon color={guestBPStatus.dotColor} />}
                sx={{
                  margin: theme.spacing(0, 2),
                  backgroundColor: guestBPStatus.bg,
                  "&:hover": {backgroundColor: guestBPStatus.bg},
                }}
              />
            </FlexContainer>
            {isActionInProgress && loadingAction.type === "bp_dropdown" ? (
              <CircularSpinner
                size="20px"
                style={{marginRight: theme.spacing(3)}}
              />
            ) : (
              <SvgIcon
                onClick={(e) => {
                  e.stopPropagation();
                  setBpDropdownAnchorEl(e.currentTarget);
                }}
                component={ICONS["more-vertical"]}
                className={classes.moreIcon}
                sx={{color: theme.colors.textSecondary}}
              />
            )}
          </FlexContainer>
        }
        divider={false}
        expanded={expanded}
        onExpand={() => setExpanded(!expanded)}
      >
        {vfActions.length === 0 ? (
          <Skeleton width={"100%"} height={"30px"} />
        ) : expanded ? (
          expandedGuestJourney()
        ) : (
          collapsedGuestJourney()
        )}
      </DropdownExpand>
    </Container>
  );
};

export default BoardingPassCard;
